"use client";

import type { CompetitionData } from "@/app/components/ActiveCompetitions/types";
import { useActiveCompetitions } from "@/app/redux/features/categories/selectors";
import { useMemo } from "react";

/** Normalise a path or full URL for comparison against `CompetitionData.PageUrl`. */
export function normalizePageUrl(url: string): string {
  if (!url) return "";

  let path = url.trim();

  try {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      path = new URL(path).pathname;
    }
  } catch {
    // Fall through — treat as a path fragment.
  }

  const queryIndex = path.indexOf("?");
  if (queryIndex >= 0) path = path.slice(0, queryIndex);

  const hashIndex = path.indexOf("#");
  if (hashIndex >= 0) path = path.slice(0, hashIndex);

  if (!path.startsWith("/")) path = `/${path}`;

  return path.replace(/\/+$/, "") || "/";
}

/** Finds the active competition whose `PageUrl` matches the given page path. */
export function findCompetitionByPageUrl(
  competitions: CompetitionData[],
  pageUrl: string
): CompetitionData | undefined {
  const normalizedPageUrl = normalizePageUrl(pageUrl);
  if (!normalizedPageUrl) return undefined;

  return competitions.find(competition => normalizePageUrl(competition.PageUrl) === normalizedPageUrl);
}

/**
 * Resolves the active competition for the current (or supplied) page URL using
 * data from `/getactivecompetitions` already stored in Redux.
 */
export default function useCompetitionByPageUrl(pageUrl?: string): CompetitionData | undefined {
  const activeCompetitions = useActiveCompetitions();

  return useMemo(() => {
    const resolvedPageUrl = pageUrl ?? (typeof window !== "undefined" ? window.location.pathname : "");
    return findCompetitionByPageUrl(activeCompetitions, resolvedPageUrl);
  }, [activeCompetitions, pageUrl]);
}
